import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, IconButton, Typography, Box, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { Button } from 'components/index';
import { styled } from '@mui/material/styles';
import { FileUploadErrorDTO, MissingFieldWarningDTO } from './project.type';

interface ErrorProps {
  errors: FileUploadErrorDTO[];
  onRemoveError: (error: string) => void;
  onDiscardAllErrors: () => void;
  missingFieldWarnings?: MissingFieldWarningDTO[];
  onHideMissingFieldWarning?: (fieldKey: string) => void;
}

const HoverableRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// One row of a grouped warning list — either an upload/validation error (dismiss removes it from
// project.errors) or a missing-required-field warning (dismiss hides it via HiddenFieldWarnings).
// Kept as a union so both kinds render in the same accordion/group instead of two separate
// "Warnings" sections.
type WarningRow =
  | { kind: 'error'; formatted: React.ReactNode; key: string; onDismiss: () => void }
  | { kind: 'missingField'; formatted: React.ReactNode; key: string; onDismiss: () => void };

const ProjectErrors: React.FC<ErrorProps> = ({
  errors,
  onRemoveError,
  onDiscardAllErrors,
  missingFieldWarnings = [],
  onHideMissingFieldWarning,
}) => {
  function formatErrorText(text: string): React.ReactNode {
    const parts: React.ReactNode[] = [];
    text?.split(/(\^.*?\^)/g).forEach((part, index) => {
      if (part.startsWith('^') && part.endsWith('^')) {
        parts.push(
          <span key={index} style={{ fontWeight: 400, fontStyle: 'italic' }}>
            {"'" + part.slice(1, -1) + "'"}
          </span>,
        );
      } else {
        parts.push(part);
      }
    });

    return <>{parts}</>;
  }

  const groupMap = new Map<string, WarningRow[]>();
  const addRow = (groupName: string, row: WarningRow) => {
    if (!groupMap.has(groupName)) groupMap.set(groupName, []);
    groupMap.get(groupName)?.push(row);
  };

  errors.forEach((error, i) => {
    const drawMatch = error?.message?.match(/Draw: (.*?) -/);
    const drawName = drawMatch ? drawMatch[1] : 'General Warnings';
    const message = drawMatch ? error.message.replace(`Draw: ${drawName} - `, '') : error.message;
    addRow(drawName, {
      kind: 'error',
      formatted: formatErrorText(message),
      key: `error-${i}`,
      onDismiss: () => onRemoveError(error.message),
    });
  });

  missingFieldWarnings.forEach((warning, i) => {
    const groupName = warning.draw || 'General Warnings';
    addRow(groupName, {
      kind: 'missingField',
      formatted: warning.message,
      key: `missing-field-${i}`,
      onDismiss: () => onHideMissingFieldWarning?.(warning.fieldKey),
    });
  });

  return (
    <Accordion elevation={0} sx={{ backgroundColor: 'rgba(255, 204, 153, 0.4)', mb: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <WarningIcon color='warning' sx={{ mr: 2 }} />
          <Typography variant='h5'>Warnings</Typography>
        </Box>
        <Button
          variant='outlined'
          color='error'
          onClick={(event: any) => {
            event.stopPropagation();
            onDiscardAllErrors();
          }}
          sx={{ ml: 'auto', mr: 2 }}
        >
          Discard All
        </Button>
      </AccordionSummary>
      <AccordionDetails sx={{ flexDirection: 'column', pt: 0, pb: 2, pl: 2, pr: 2 }}>
        <Divider sx={{ my: 0 }} />
        {Array.from(groupMap.entries()).map(([key, rows], index) => (
          <Box key={index} sx={{ mt: 2, ml: 4 }}>
            <Typography variant='h6'>{key}</Typography>
            {rows.map(row => (
              <HoverableRow key={row.key}>
                <Typography variant='body2'>{row.formatted}</Typography>
                <IconButton onClick={row.onDismiss} aria-label='delete' size='small'>
                  <CloseIcon />
                </IconButton>
              </HoverableRow>
            ))}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default ProjectErrors;
